export interface GaplessMetadata {
  encoderDelay: number;
  endPadding: number;
  originalSamples: number;
  totalSamples: number;
}

export async function extractGaplessMetadata(url: string): Promise<GaplessMetadata> {
  try {
    // First, try fetching the initial portion (first 20KB for most files)
    let response = await fetch(url, {
      headers: { Range: 'bytes=0-20000' }
    });

    let arrayBuffer = await response.arrayBuffer();

    try {
      const metadata = await parseGaplessMetadata(arrayBuffer);

      // Calculate beat information for sync analysis
      const sampleRate = 48000;
      const bpm = 100; // All tracks encoded at 100 BPM
      const samplesPerBeat = (60 * sampleRate) / bpm; // 28,800 samples per beat
      const exactBeats = metadata.originalSamples / samplesPerBeat;
      const isExactBeats = Math.abs(exactBeats - Math.round(exactBeats)) < 0.001;

      // Log parsed values for debugging
      console.log(`📊 Parsed gapless metadata for ${url}:`, {
        encoderDelay: metadata.encoderDelay,
        endPadding: metadata.endPadding,
        originalSamples: metadata.originalSamples,
        originalLengthSeconds: (metadata.originalSamples / 48000).toFixed(3),
        exactBeats: exactBeats.toFixed(6),
        roundedBeats: Math.round(exactBeats),
        isExactBeats: isExactBeats,
        samplesPerBeat: samplesPerBeat,
        beatDeviation: (exactBeats - Math.round(exactBeats)).toFixed(6)
      });

      return metadata;
    } catch (error) {
      // If not found in first 20KB, try fetching more for longer files
      console.log('📊 Metadata not found in first 20KB, fetching first 50KB for:', url);
      response = await fetch(url, {
        headers: { Range: 'bytes=0-50000' }
      });
      arrayBuffer = await response.arrayBuffer();

      const metadata = await parseGaplessMetadata(arrayBuffer);

      // Calculate beat information for sync analysis
      const sampleRate = 48000;
      const bpm = 100; // All tracks encoded at 100 BPM
      const samplesPerBeat = (60 * sampleRate) / bpm; // 28,800 samples per beat
      const exactBeats = metadata.originalSamples / samplesPerBeat;
      const isExactBeats = Math.abs(exactBeats - Math.round(exactBeats)) < 0.001;

      // Log parsed values for debugging
      console.log(`📊 Parsed gapless metadata for ${url}:`, {
        encoderDelay: metadata.encoderDelay,
        endPadding: metadata.endPadding,
        originalSamples: metadata.originalSamples,
        originalLengthSeconds: (metadata.originalSamples / 48000).toFixed(3),
        exactBeats: exactBeats.toFixed(6),
        roundedBeats: Math.round(exactBeats),
        isExactBeats: isExactBeats,
        samplesPerBeat: samplesPerBeat,
        beatDeviation: (exactBeats - Math.round(exactBeats)).toFixed(6)
      });

      return metadata;
    }
  } catch (error) {
    console.warn(`⚠️ Failed to parse metadata for ${url}, using defaults:`, error);
    // Return sensible defaults if parsing fails
    return {
      encoderDelay: 2112, // Common Apple AAC delay
      endPadding: 960, // Common Apple AAC padding
      originalSamples: 0, // Will need to be calculated differently
      totalSamples: 0
    };
  }
}

export async function parseGaplessMetadata(buffer: ArrayBuffer): Promise<GaplessMetadata> {
  // Use text-based search which is more robust for finding iTunSMPB
  const uint8Array = new Uint8Array(buffer); // Check entire buffer
  const decoder = new TextDecoder('ascii', { fatal: false }); // Don't throw on invalid sequences
  const text = decoder.decode(uint8Array);

  // Find iTunSMPB atom
  const iTunSMPBIndex = text.indexOf('iTunSMPB');
  if (iTunSMPBIndex === -1) {
    throw new Error('No iTunSMPB metadata found');
  }

  // The data starts after 'iTunSMPB' - skip any non-printable bytes
  let dataStart = iTunSMPBIndex + 8;
  while (
    dataStart < text.length &&
    (text.charCodeAt(dataStart) < 0x20 || text.charCodeAt(dataStart) > 0x7e)
  ) {
    dataStart++;
  }

  const metadataString = text.substring(dataStart, Math.min(dataStart + 200, text.length));
  console.log('🔍 Found iTunSMPB metadata string:', metadataString.substring(0, 100));

  // Try multiple patterns - different encoders use different formats
  // Pattern 1: Standard format with spaces " 00000000 00000840 000003C0 0000000000070800"
  let pattern = /\s*([0-9A-Fa-f]{8})\s+([0-9A-Fa-f]{8})\s+([0-9A-Fa-f]{8})\s+([0-9A-Fa-f]{16})/;
  let match = metadataString.match(pattern);

  if (!match) {
    // Pattern 2: Some encoders use shorter format without the 16-digit field
    pattern = /\s*([0-9A-Fa-f]{8})\s+([0-9A-Fa-f]{8})\s+([0-9A-Fa-f]{8})\s+([0-9A-Fa-f]{8})/;
    match = metadataString.match(pattern);
  }

  if (!match) {
    // Pattern 3: Try without the first padding field
    pattern = /([0-9A-Fa-f]{8})\s+([0-9A-Fa-f]{8})\s+([0-9A-Fa-f]{8,16})/;
    const altMatch = metadataString.match(pattern);
    if (altMatch) {
      // Construct match array for consistent processing
      match = ['', '00000000', altMatch[1], altMatch[2], altMatch[3]];
    }
  }

  if (!match) {
    console.error('🔍 Failed to parse iTunSMPB format from:', metadataString.substring(0, 100));
    throw new Error('Invalid iTunSMPB format');
  }

  console.log('🔍 Parsed iTunSMPB parts:', match.slice(1));

  // Parse the matched groups
  const [, prePadding, encoderDelayHex, endPaddingHex, originalSamplesHex] = match;

  const metadata: GaplessMetadata = {
    encoderDelay: parseInt(encoderDelayHex, 16) || 0,
    endPadding: parseInt(endPaddingHex, 16) || 0,
    originalSamples: originalSamplesHex ? parseInt(originalSamplesHex, 16) : 0,
    totalSamples: 0 // Will be set by the caller based on decoded audio
  };

  // Log the parsed values
  console.log(
    `📊 Parsed gapless metadata: encoderDelay=${metadata.encoderDelay}, endPadding=${metadata.endPadding}, originalSamples=${metadata.originalSamples}`
  );

  return metadata;
}
